/*
| Hover/focus prefetch for internal nav — warms the cache before the click so
| cross-document navigation feels instant (pairs with the View Transition).
| Guards: same-origin GET nav only, once per URL/session, 80ms debounce, skip
| _blank/download/external + mutations/exports/api; respect Save-Data.
*/
export function initPrefetch(): void {
    const conn = (navigator as any).connection;
    if (conn?.saveData || window.matchMedia?.('(prefers-reduced-data: reduce)').matches) {
        return;
    }

    const done = new Set<string>();
    let timer: number | undefined;

    const candidate = (a: HTMLAnchorElement | null): string | null => {
        if (!a || a.target === '_blank' || a.hasAttribute('download') || !a.href) {
            return null;
        }
        let url: URL;
        try {
            url = new URL(a.href, location.href);
        } catch {
            return null;
        }
        if (url.origin !== location.origin) {
            return null;
        }
        if (url.pathname === location.pathname && url.search === location.search) {
            return null; // current page
        }
        // Never prefetch mutations, exports, downloads or the API — only readable nav.
        if (/\/(api|logout|auth|export)(\/|$)/.test(url.pathname) || /\.(xlsx|csv|pdf|ics)$/i.test(url.pathname)) {
            return null;
        }
        return url.href;
    };

    const prefetch = (href: string): void => {
        if (done.has(href)) {
            return;
        }
        done.add(href);
        const link = document.createElement('link');
        link.rel = 'prefetch';
        link.href = href;
        document.head.appendChild(link);
    };

    const onIntent = (e: Event): void => {
        const a = (e.target as HTMLElement)?.closest?.('a');
        const href = candidate(a as HTMLAnchorElement | null);
        if (!href) {
            return;
        }
        window.clearTimeout(timer);
        timer = window.setTimeout(() => prefetch(href), 80);
    };

    document.addEventListener('pointerover', onIntent);
    document.addEventListener('focusin', onIntent);
    document.addEventListener('touchstart', onIntent, { passive: true });
}
